import { useCallback, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import UIEditorPage from 'UIComponents/page/UIEditorPage';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import AwardVisibilityToggleContainer from '../containers/Achievements/AwardVisibilityToggleContainer';
import { EmbedAward, useMaybeCreateOrUpdateAchievement } from '../features/achievement';
import { SendToUpwork } from '../features/upwork';
import { getAwardModalDefaultPath, getAchievementPreviewPath } from '../utils/RoutingUtils';
import AwardShareHeader from '../features/achievement/components/AwardShareHeader';
import SocialShareButtons from '../features/achievement/components/SocialShareButtons';
import UsageTracker from '../utils/UsageTracker';
import { NavMarker } from 'react-rhumb';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import { useTrackAwardProgress, useTrackProgressById } from '../features/userProgress';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledEditorPage = styled(UIEditorPage).withConfig({
  displayName: "AwardShareControls__StyledEditorPage",
  componentId: "oyfq0e-0"
})(["position:absolute;top:0;bottom:0;left:0;right:0;z-index:5;"]);
const BorderedArea = styled(UIFlex).withConfig({
  displayName: "AwardShareControls__BorderedArea",
  componentId: "oyfq0e-1"
})(["border-top:1px solid ", ";padding:24px 0 20px;max-width:948px;margin:0 auto;"], BATTLESHIP);
const AwardShareControls = () => {
  const location = useLocation();
  const {
    trackId
  } = useParams();
  const {
    trackProgressByIdLoading
  } = useTrackProgressById(Number(trackId));
  const {
    award,
    allProgressLoading
  } = useTrackAwardProgress(Number(trackId));
  const {
    track,
    trackLoading,
    achievementsLoading,
    achievementsError,
    isAwardInvalid
  } = useMaybeCreateOrUpdateAchievement(trackId);
  const anyLoading = achievementsLoading || trackLoading || trackProgressByIdLoading || allProgressLoading;
  const previewPath = getAchievementPreviewPath(trackId);
  useEffect(() => {
    if (track) {
      UsageTracker.track('awardInteraction', {
        action: 'view-sharing-controls',
        trackId,
        trackName: track.name,
        view: 'sharing-controls'
      });
    }
  }, [award, track, trackId]);
  const handleCopyAwardCode = useCallback(() => {
    UsageTracker.track('awardInteraction', {
      action: 'copy-badge-code',
      trackId,
      trackName: track && track.name,
      view: 'sharing-controls'
    });
  }, [track, trackId]);
  if (anyLoading) {
    return /*#__PURE__*/_jsx(UILoadingOverlay, {
      "data-test-id": "loading-spinner"
    });
  }
  if (isAwardInvalid) {
    return null;
  }
  return /*#__PURE__*/_jsx("div", {
    style: {
      minHeight: '100vh'
    },
    children: /*#__PURE__*/_jsx(NavMarker, {
      name: "AWARD_SHARE_CONTROLS_LOADED",
      children: /*#__PURE__*/_jsxs(StyledEditorPage, {
        "data-test-id": "award-share-controls",
        headerComponent: /*#__PURE__*/_jsx(AwardShareHeader, {
          backLinkHref: location.state && location.state.backFromAwardShare ? location.state.backFromAwardShare.pathname : getAwardModalDefaultPath(trackId)
        }),
        children: [/*#__PURE__*/_jsx(UIFlex, {
          className: "m-top-15 m-bottom-5",
          justify: "center",
          wrap: "wrap",
          children: /*#__PURE__*/_jsx(SocialShareButtons, {
            language: track.language,
            trackId: Number(trackId)
          })
        }), /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          className: "m-y-5",
          justify: "center",
          children: [/*#__PURE__*/_jsx(UIBox, {
            className: "m-x-4",
            children: /*#__PURE__*/_jsx(AwardVisibilityToggleContainer, {
              award: award,
              disabledTooltip: "learningCenter.awardShareControls.disabledToggleTooltip",
              isFetchingAchievements: achievementsLoading,
              language: track.language,
              trackId: trackId
            })
          }), /*#__PURE__*/_jsx(UIBox, {
            basis: 480,
            shrink: 2,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.awardShareControls.sharingConsent"
            })
          })]
        }), !achievementsLoading && /*#__PURE__*/_jsx("div", {
          className: "text-center m-top-20",
          children: /*#__PURE__*/_jsx(UIRouterLink, {
            className: "p-y-4",
            "data-test-id": "open-achievement-preview",
            disabled: !!achievementsError,
            to: previewPath,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.awardShareControls.previewCert"
            })
          })
        }), /*#__PURE__*/_jsxs("section", {
          className: "p-top-15",
          children: [/*#__PURE__*/_jsx(BorderedArea, {
            children: /*#__PURE__*/_jsx(EmbedAward, {
              language: track.language,
              onCopyAwardCode: handleCopyAwardCode,
              trackId: Number(trackId)
            })
          }), track && track.upworkCode && /*#__PURE__*/_jsx(BorderedArea, {
            children: /*#__PURE__*/_jsx(SendToUpwork, {
              language: track.language,
              trackId: Number(trackId)
            })
          })]
        })]
      })
    })
  });
};
export default AwardShareControls;